import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { MapContainer, Marker, Popup, TileLayer, useMapEvents, WMSTileLayer } from "react-leaflet";
import { LatLng } from "leaflet";
import Resources from "../../../../types/Resources";
import PositionPickerRow, { PositionPickerRow_default } from "../../../../types/forms/Permits/PositionPickerRow";

type CoordinatesModalProps = {
    show: boolean;
    position: PositionPickerRow | undefined;
    onClose: () => void;
    onPositionChange: (updatedPosition: PositionPickerRow) => void;
    resources: Resources;
};

function CoordinatesModal({
    show,
    position,
    onClose,
    onPositionChange: onPositionUpdate,
    resources
}: CoordinatesModalProps) {
    const [selectedLatitude, setSelectedLatitude] = useState<number>();
    const [selectedLongitude, setSelectedLongitude] = useState<number>();

    const markerRef = useRef<L.Marker>(null);

    useEffect(() => {
        if (position && show) {
            setSelectedLatitude(position.latitude);
            setSelectedLongitude(position.longitude);
        }
    }, [position, show]);

    useEffect(() => {
        if (selectedLatitude && selectedLongitude && markerRef.current) {
            markerRef.current.openPopup();
        }
    }, [selectedLatitude, selectedLongitude]);

    function mapClickHandler(clickedLatLng: LatLng) {
        setSelectedLatitude(clickedLatLng.lat);
        setSelectedLongitude(clickedLatLng.lng);
    }

    function savePositionClickHandler() {
        if (!selectedLatitude || !selectedLongitude) {
            return;
        }

        const trimmedLongitude = parseFloat(selectedLongitude.toFixed(6));
        const trimmedLatitude = parseFloat(selectedLatitude.toFixed(6));

        const updatedPosition: PositionPickerRow = !!position
            ? {
                  ...position,
                  latitude: trimmedLatitude,
                  longitude: trimmedLongitude
              }
            : PositionPickerRow_default();

        onPositionUpdate(updatedPosition);
        onClose();
    }

    return (
        <Modal show={show} onHide={onClose} size="xl">
            <Modal.Header closeButton={true}>
                <Modal.Title>{resources.get("ChooseLocation")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="rounded-bottom p-0 overflow-hidden">
                <div className="container-fluid p-0">
                    <div className="col-12">
                        <MapContainer
                            center={[57.688111, 11.848859]}
                            zoom={14}
                            style={{ height: "80vh" }}
                            attributionControl={false}
                        >
                            {selectedLatitude && selectedLongitude ? (
                                <Marker position={[selectedLatitude, selectedLongitude]} ref={markerRef}>
                                    <Popup>
                                        <div className="form-group">
                                            <label className="form-label fw-bold" style={{ whiteSpace: "nowrap" }}>
                                                {resources.get("Latitude")}: {selectedLatitude.toFixed(6)}
                                            </label>
                                            <br />
                                            <label className="form-label fw-bold" style={{ whiteSpace: "nowrap" }}>
                                                {resources.get("Longitude")}: {selectedLongitude.toFixed(6)}
                                            </label>
                                            <div className="d-flex justify-content-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-success"
                                                    style={{ whiteSpace: "nowrap" }}
                                                    onClick={savePositionClickHandler}
                                                >
                                                    {resources.get("SavePosition")}
                                                </button>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ) : null}
                            <MapClickLayer onClick={mapClickHandler} />
                            <WMSTileLayer
                                url="https://opengeodata.goteborg.se/services/ortofoto/wms/v1"
                                layers="orto_2023"
                                attribution={""}
                                maxNativeZoom={19}
                                maxZoom={19}
                                minZoom={13}
                            ></WMSTileLayer>
                        </MapContainer>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

type MapClickLayerProps = {
    onClick: (latLng: LatLng) => void;
};

// Event handlers are registered with the hook useMapEvents, which can only be used in
// components that are descendants of a MapContainer. I.e. CoordinatesPickerModal cannot
// call useMapEvents itself.
function MapClickLayer({ onClick }: MapClickLayerProps) {
    useMapEvents({
        click: (e: any) => onClick(e.latlng)
    });
    return <></>;
}

export default CoordinatesModal;
